import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import Header from '@shared/components/layout/Header';
import SEOHead from '@shared/components/common/SEOHead';
import SearchForm from '@features/keywords/components/SearchForm';
import KeywordDataView from '@features/keywords/components/KeywordDataView';
import KeywordsFloatingBar from '@features/keywords/components/KeywordsFloatingBar';
import FAQ from '@shared/components/common/FAQ';
import PreviewComingSoon from '@shared/components/common/PreviewComingSoon';
// import StatsSection from '@shared/components/common/StatsSection';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import {
  enqueueSuggest,
  fetchJobKeywords,
  fetchPublicKeywordsStats,
  pollJobStatus,
} from '@features/keywords/api/keywordsApi';
import { browserWorkerService } from '@features/keywords/services/browserWorkerService';
import { getKeywordsFoundEmoji } from '@features/keywords/utils/keywordsFoundEmoji';
import type { CrawlProgress, KeywordData, SearchSubmitParams } from '@features/keywords/types/keyword.types';

type ViewMode = 'table' | 'list' | 'card' | 'chart' | 'tree';

const KEYWORDS_COUNT_FALLBACK = 20000;

interface KeywordDataViewRef {
  getRowData: () => any[];
  exportToCSV: () => void;
  downloadSVG: () => void;
  copyKeywords: () => Promise<void>;
  toggleFullscreen: () => void;
}

const JOB_POLL_MS = 1500;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

const AppContent: React.FC = () => {
  const { translations, isRTL, language, t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [quickFilterText, setQuickFilterText] = useState('');
  const keywordDataViewRef = useRef<KeywordDataViewRef>(null);
  const [keywordData, setKeywordData] = useState<KeywordData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchProgress, setSearchProgress] = useState<CrawlProgress | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const keywordOffsetRef = useRef(0);
  const [count, setCount] = useState(1);
  const [targetCount, setTargetCount] = useState(KEYWORDS_COUNT_FALLBACK);
  const duration = 3000;

  const mergeKeywords = useCallback((batch: KeywordData[]) => {
    if (!batch.length) return;
    setKeywordData((prev) => {
      const existing = new Set(prev.map((item) => item.keyword));
      const newRows = batch.filter((item) => !existing.has(item.keyword));
      return newRows.length > 0 ? [...prev, ...newRows] : prev;
    });
  }, []);

  const loadAllKeywords = useCallback(
    async (jobId: string, signal?: AbortSignal) => {
      let offset = 0;
      while (!signal?.aborted) {
        const page = await fetchJobKeywords(jobId, offset);
        mergeKeywords(page.keywords);
        if (!page.hasMore) break;
        offset = page.offset + page.keywords.length;
      }
    },
    [mergeKeywords],
  );

  const handleQuickFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuickFilterText(e.target.value);
  };

  const handleSearch = useCallback(
    async ({ keyword, language: lang, country }: SearchSubmitParams) => {
      searchAbortRef.current?.abort();
      browserWorkerService.stop();

      const controller = new AbortController();
      searchAbortRef.current = controller;
      keywordOffsetRef.current = 0;

      setIsSearching(true);
      setHasSearched(true);
      setSearchProgress(null);
      setStatusMessage(null);
      setKeywordData([]);

      try {
        const enqueue = await enqueueSuggest({
          seed: keyword,
          language_code: lang,
          location_code: country,
        });

        if (controller.signal.aborted) return;

        const searchStartedAt = Date.now();
        const escalateAfterSec = enqueue.browserWorker?.escalateAfterSec ?? 0;

        if (enqueue.browserWorker?.enabled) {
          browserWorkerService.claimOwnJob({
            jobId: enqueue.jobId,
            hint: enqueue.browserWorker,
            searchStartedAt,
            signal: controller.signal,
            onEscalate: () => {
              setStatusMessage(
                language === 'fa'
                  ? 'در حال پردازش توسط سرور…'
                  : 'Processing on server…',
              );
            },
          });
        }

        if (enqueue.cached || enqueue.status === 'completed') {
          await loadAllKeywords(enqueue.jobId, controller.signal);
          return;
        }

        while (!controller.signal.aborted) {
          const status = await pollJobStatus(enqueue.jobId);
          if (status.progress) {
            setSearchProgress(status.progress);
          }

          const page = await fetchJobKeywords(
            enqueue.jobId,
            keywordOffsetRef.current,
          );
          if (page.keywords.length > 0) {
            mergeKeywords(page.keywords);
            keywordOffsetRef.current = page.offset + page.keywords.length;
          }

          if (status.status === 'completed' || status.status === 'failed') {
            browserWorkerService.stopClaimPoll();
            if (status.status === 'failed') {
              console.error('Keyword job failed:', status.error);
            }
            break;
          }

          if (status.status === 'active') {
            browserWorkerService.stopClaimPoll();
          }

          if (
            escalateAfterSec > 0 &&
            status.status === 'waiting' &&
            Date.now() - searchStartedAt >= escalateAfterSec * 1000
          ) {
            setStatusMessage(
              language === 'fa'
                ? 'در حال پردازش توسط سرور…'
                : 'Processing on server…',
            );
            browserWorkerService.stopClaimPoll();
          }

          await sleep(JOB_POLL_MS, controller.signal);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Keyword search failed:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    },
    [language, loadAllKeywords, mergeKeywords],
  );

  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
      browserWorkerService.stop();
    };
  }, []);

  const handleExportCSV = () => {
    if (viewMode === 'chart' || viewMode === 'tree') {
      if (keywordDataViewRef.current?.downloadSVG) {
        keywordDataViewRef.current.downloadSVG();
      }
    } else if (keywordDataViewRef.current?.exportToCSV) {
      keywordDataViewRef.current.exportToCSV();
    }
  };

  const handleCopyKeywords = async () => {
    await keywordDataViewRef.current?.copyKeywords?.();
  };

  const handleFullscreen = () => {
    keywordDataViewRef.current?.toggleFullscreen?.();
  };

  useEffect(() => {
    fetchPublicKeywordsStats()
      .then((stats) => {
        if (stats.keywords_discovered > 0) {
          setTargetCount(stats.keywords_discovered);
        }
      })
      .catch((err) => {
        console.error('Failed to load keywords stats:', err);
        setTargetCount(KEYWORDS_COUNT_FALLBACK);
      });
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    const startValue = 1;
    const endValue = targetCount;
    const difference = endValue - startValue;

    const updateCount = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + difference * easeOut);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        setCount(endValue);
      }
    };

    updateCount();
  }, [targetCount, duration]);

  const keywordsFoundCount = Math.max(
    keywordData.length,
    searchProgress?.keywordsFound ?? 0,
  );

  const formattedKeywordsCount = keywordsFoundCount.toLocaleString(
    language === 'fa' ? 'fa-IR' : 'en-US',
  );
  const keywordsFoundEmoji = getKeywordsFoundEmoji(keywordsFoundCount);

  return (
    <>
      <SEOHead />
      <div className={`min-h-screen bg-gradient-to-b from-white via-purple-50 to-purple-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
        <Header />

        <main className="relative pt-8">
          <div className="relative z-10 w-full">
            <div className="container mx-auto px-4">
            <div
              className={`transition-all duration-500 ease-in-out ${
                hasSearched ? 'pt-2 md:pt-4' : 'pt-10 md:pt-14 lg:pt-16'
              }`}
            >
              <div
                className={`text-center transition-all duration-500 ease-in-out ${
                  hasSearched ? 'mb-4 mt-2 md:mt-4' : 'mb-8 md:mb-10'
                }`}
              >
                <h1
                  className={`font-extrabold text-gray-900 dark:text-white transition-all duration-500 ease-in-out ${
                    hasSearched ? 'text-xl md:text-2xl mb-3' : 'text-3xl md:text-4xl mb-6'
                  }`}
                >
                  {translations.h1}
                </h1>
                <p
                  className={`text-gray-700 dark:text-gray-300 max-w-3xl mx-auto transition-all duration-500 ease-in-out ${
                    hasSearched ? 'text-sm md:text-base' : 'text-lg md:text-xl'
                  }`}
                >
                  {translations.description}
                </p>
              </div>

              <div className={`relative ${hasSearched ? 'mb-2 md:mb-4' : ''}`}>
                <SearchForm
                  onSearch={handleSearch}
                  isSearching={isSearching}
                  searchProgress={searchProgress}
                  statusMessage={statusMessage}
                  keywordsFoundCount={keywordData.length}
                  compact={hasSearched}
                />
              </div>
            </div>
            </div>
          </div>

          <div className="bg-primary relative z-0 w-screen max-w-[100vw] ml-[calc(50%-50vw)] overflow-visible pb-4">
            <div
              className="keywordyab-hero-wave pointer-events-none absolute inset-x-0 bottom-full w-full"
              aria-hidden="true"
            >
              <img
                src="/img/bg-chart-p.svg"
                alt=""
                className="block w-full h-auto select-none"
                draggable={false}
              />
            </div>
            {hasSearched && (
              <div className="container relative z-20 mx-auto px-4 pt-4 md:pt-6 search-results-enter pb-2">
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-4" dir="ltr">
              <div className={`flex shrink-0 w-full md:w-auto justify-center md:justify-start ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-3 items-center`}>
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm`}>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'table'
                        ? 'bg-primary text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    data-tooltip-id="view-mode-tooltip"
                    data-tooltip-content={t('views.table')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                        ? 'bg-primary text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    data-tooltip-id="view-mode-tooltip"
                    data-tooltip-content={t('views.list')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('card')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'card'
                        ? 'bg-primary text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    data-tooltip-id="view-mode-tooltip"
                    data-tooltip-content={t('views.card')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('chart')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'chart'
                        ? 'bg-primary text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    data-tooltip-id="view-mode-tooltip"
                    data-tooltip-content={t('views.chart')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v10l6 6" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('tree')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'tree'
                        ? 'bg-primary text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    data-tooltip-id="view-mode-tooltip"
                    data-tooltip-content={t('views.tree')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(-90deg)' }}>
                      <rect x="8" y="2" width="8" height="6" rx="1" strokeWidth="2" />
                      <rect x="3" y="12" width="8" height="6" rx="1" strokeWidth="2" />
                      <rect x="13" y="12" width="8" height="6" rx="1" strokeWidth="2" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4M11 12l-1-1M13 12l1 1M7 12h1M16 12h1" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={handleExportCSV}
                  className={`p-2 rounded-md transition-colors bg-white dark:bg-gray-800 shadow-sm ${isDark
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  data-tooltip-id="action-tooltip"
                  data-tooltip-content={viewMode === 'chart' || viewMode === 'tree'
                    ? t('common.downloadSVG')
                    : t('common.exportCSV')
                  }
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
                <button
                  onClick={handleFullscreen}
                  className={`p-2 rounded-md transition-colors bg-white dark:bg-gray-800 shadow-sm ${isDark
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  data-tooltip-id="action-tooltip"
                  data-tooltip-content={t('common.fullscreen')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              </div>

              <div
                className={`flex w-full md:flex-1 justify-center items-center min-w-0 px-0 md:px-2 ${isRTL ? 'rtl' : 'ltr'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <p className="text-white/90 text-sm md:text-base text-center whitespace-normal md:whitespace-nowrap flex flex-wrap items-center justify-center gap-2">
                  <span className="text-2xl md:text-3xl leading-none" aria-hidden="true">
                    {keywordsFoundEmoji}
                  </span>
                  <span className="text-3xl md:text-4xl font-bold text-white">
                    {formattedKeywordsCount}
                  </span>
                  <span>
                    {keywordsFoundCount >= 400
                      ? t('search.keywordsFoundGift')
                      : t('search.keywordsFound')}
                  </span>
                </p>
              </div>

              <div className="relative w-full md:w-[300px] md:shrink-0">
                <input
                  type="text"
                  value={quickFilterText}
                  onChange={handleQuickFilterChange}
                  placeholder={t('search.searchInTable')}
                  className={`w-full px-4 py-2 ${isRTL ? (quickFilterText ? 'pl-10' : 'pr-10') : (quickFilterText ? 'pr-10' : 'pl-10')} border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${isDark
                      ? 'bg-gray-800 text-white border-gray-700'
                      : 'bg-white text-gray-900 border-gray-300'
                    }`}
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                />
                {quickFilterText ? (
                  <button
                    onClick={() => setQuickFilterText('')}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    type="button"
                    data-tooltip-id="action-tooltip"
                    data-tooltip-content={t('common.clear')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <svg
                    className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </div>
            </div>

            <KeywordDataView
              ref={keywordDataViewRef}
              viewMode={viewMode}
              quickFilterText={quickFilterText}
              onQuickFilterChange={handleQuickFilterChange}
              data={keywordData}
              isLoading={isSearching}
              searchProgress={searchProgress}
            />
              </div>
            )}

            <div className={`container mx-auto px-4 ${hasSearched ? 'pt-6 md:pt-8' : 'pt-28 md:pt-36'}`}>

            <div className={`relative z-10 ${hasSearched ? 'mt-8' : 'mt-16 md:mt-24'}`}>
              <PreviewComingSoon />
            </div>

            <div className="relative z-10 pb-12 pt-8 md:pt-12">
              <FAQ />
            </div>

            <div className="text-center pb-12 md:pb-12 relative z-10" style={{ paddingTop: '2rem' }}>
              <p className={`text-lg md:text-xl text-white/90 ${isRTL ? 'rtl' : 'ltr'}`}>
                {language === 'fa' ? (
                  <>
                    بیش از{' '}
                    <span className="text-3xl md:text-4xl font-bold text-white">
                      {count.toLocaleString('fa-IR')}
                    </span>
                    {' '}کوئری از کیوردیاب استخراج شده؛ حالا نوبت شماست
                  </>
                ) : (
                  <>
                    Over{' '}
                    <span className="text-3xl md:text-4xl font-bold text-white">
                      {count.toLocaleString('en-US')}
                    </span>
                    {' '}queries extracted from Keywordyab; now it's your turn
                  </>
                )}
              </p>
            </div>

            <footer className="pb-4">
              <div className="container mx-auto px-4 text-center">
                <p className="text-white/90 text-sm md:text-base mb-4 max-w-3xl mx-auto">
                  {t('footer.description')}
                </p>
                <p className="text-white/90">
                  <span
                    data-tooltip-id="footer-tooltip"
                    data-tooltip-content={t('footer.tooltip')}
                  >
                    {t('footer.copyrightPrefix')}
                    <a
                      href="https://mizfa.tools/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-white transition-colors"
                    >
                      {t('footer.mizfaTools')}
                    </a>
                    {t('footer.copyrightSuffix', { year: new Date().getFullYear() })}
                  </span>
                </p>
              </div>
            </footer>
            </div>
          </div>
        </main>
      </div>
      <Tooltip
        id="footer-tooltip"
        place="top"
        positionStrategy="fixed"
        className="!bg-gray-800 !text-white !text-sm !px-3 !py-2 !rounded-lg !shadow-lg kw-app-tooltip"
        style={{ zIndex: 999999 }}
        delayShow={300}
      />
      <Tooltip
        id="view-mode-tooltip"
        place="bottom"
        positionStrategy="fixed"
        className="!bg-gray-800 !text-white !text-sm !px-3 !py-2 !rounded-lg !shadow-lg kw-app-tooltip"
        style={{ zIndex: 999999 }}
        delayShow={300}
      />
      <Tooltip
        id="action-tooltip"
        place="bottom"
        positionStrategy="fixed"
        className="!bg-gray-800 !text-white !text-sm !px-3 !py-2 !rounded-lg !shadow-lg kw-app-tooltip"
        style={{ zIndex: 999999 }}
        delayShow={300}
      />
      <KeywordsFloatingBar
        visible={hasSearched}
        count={keywordsFoundCount}
        locale={language === 'fa' ? 'fa-IR' : 'en-US'}
        copyLabel={t('common.copyKeywords')}
        downloadLabel={
          viewMode === 'chart' || viewMode === 'tree'
            ? t('common.downloadSVG')
            : t('common.exportCSV')
        }
        copiedLabel={t('common.copied')}
        downloadedLabel={t('common.downloaded')}
        onCopy={handleCopyKeywords}
        onDownload={handleExportCSV}
      />
    </>
  );
};

export default AppContent;
